import React, { createContext, useContext, useState, useEffect } from 'react';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(null);

  // Handle different DigitalOcean deployment patterns
  const getBackendURL = () => {
    // If environment variable is set, use it
    if (process.env.REACT_APP_BACKEND_URL) {
      return process.env.REACT_APP_BACKEND_URL;
    }
    
    // Based on your app spec - backend is at /pandvah-backend path
    const origin = window.location.origin;
    
    // Path-based routing (from your actual app spec)
    if (origin.includes('walrus-app') || origin.includes('ondigitalocean.app')) {
      return origin + '/pandvah-backend';
    }
    
    // Local development fallback
    if (origin.includes('localhost:3000')) {
      return 'http://localhost:8000';
    }
    
    // Default fallback
    return origin + '/pandvah-backend';
  };
  
  const API_BASE_URL = getBackendURL();

  // Initialize token from localStorage on mount
  useEffect(() => {
    const storedToken = localStorage.getItem('token');
    if (storedToken) {
      setToken(storedToken);
    } else {
      setLoading(false);
    }
  }, []);

  // Fetch user profile when token changes
  useEffect(() => {
    if (token) {
      fetchUserProfile();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchUserProfile = async () => {
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/me`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const userData = await response.json();
        console.log('👤 User data loaded:', {
          email: userData.email,
          role: userData.role,
          role_name: userData.role_name,
          has_custom_role: userData.has_custom_role,
          has_permissions: !!userData.permissions,
          permissions: userData.permissions
        });
        setUser(userData);
      } else if (response.status === 401 || response.status === 403) {
        // Token is invalid or expired
        console.log('Token invalid, clearing authentication');
        localStorage.removeItem('token');
        setToken(null);
        setUser(null);
      } else {
        console.error('Error fetching user profile:', response.status);
      }
    } catch (error) {
      console.error('Network error fetching user profile:', error);
      // Don't clear token on network errors, user might be offline
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      });

      if (response.ok) {
        const data = await response.json();
        const newToken = data.access_token;
        
        // Store token in localStorage and state
        localStorage.setItem('token', newToken);
        setToken(newToken);
        
        // The useEffect will trigger fetchUserProfile when token changes
        return { success: true };
      } else {
        const errorData = await response.json();
        return { success: false, error: errorData.detail || 'Login failed' };
      }
    } catch (error) {
      console.error('Login error:', error);
      return { success: false, error: 'Network error. Please try again.' };
    }
  };

  const register = async (email, password, fullName, role = 'user', isEmployeeAlso = false) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
          password,
          full_name: fullName,
          role,
          is_employee_also: role === 'owner' ? isEmployeeAlso : false
        }),
      });

      if (response.ok) {
        const userData = await response.json();
        return { success: true, user: userData };
      } else {
        const errorData = await response.json();
        return { success: false, error: errorData.detail || 'Registration failed' };
      }
    } catch (error) {
      console.error('Registration error:', error);
      return { success: false, error: 'Network error. Please try again.' };
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
    setLoading(false);
  };

  const isAdmin = () => {
    return user && user.role === 'admin';
  };

  const isManager = () => {
    if (!user) return false;
    // Check custom role name or base role
    return user.role_name?.toLowerCase() === 'manager' || user.role === 'manager';
  };

  const isTechnician = () => {
    if (!user) return false;
    // Check custom role name or base role
    return user.role_name?.toLowerCase() === 'technician' || user.role === 'technician';
  };

  const hasRole = (role) => {
    if (!user) return false;
    // Check custom role name or base role
    return user.role_name?.toLowerCase() === role.toLowerCase() || user.role === role;
  };

  const canAccessAdmin = () => {
    return user && user.role === 'admin';
  };

  const canAccessManager = () => {
    if (!user) return false;
    
    // Admin always has access
    if (user.role === 'admin') return true;
    
    // Check custom role name
    if (user.role_name) {
      const roleName = user.role_name.toLowerCase();
      return roleName === 'owner' || roleName === 'manager';
    }
    
    // Check base role
    return user.role === 'owner' || user.role === 'manager';
  };

  const canAccessTechnician = () => {
    if (!user) return false;
    
    // Admin always has access
    if (user.role === 'admin') return true;
    
    // Check custom role name
    if (user.role_name) {
      const roleName = user.role_name.toLowerCase();
      return roleName === 'owner' || roleName === 'manager' || roleName === 'technician';
    }
    
    // Check base role
    return user.role === 'owner' || user.role === 'manager' || user.role === 'technician';
  };

  const value = {
    user,
    token,
    login,
    register,
    logout,
    isAdmin,
    isManager,
    isTechnician,
    hasRole,
    canAccessAdmin,
    canAccessManager,
    canAccessTechnician,
    loading,
    API_BASE_URL
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};